<template>
  <div class="week-day pl-2 pr-2">
    <div class="week-day-header text-center pt-3 pb-3">
      <strong>
        {{ dayHumanReadableName }}<br>
        <span class="text-primary">
          {{ dayName }}
        </span>
      </strong><br>

      <small :class="{
        'text-danger': dayQty > 3500,
        'text-warning': dayQty > 2500 && dayQty <= 3500,
        'text-success': dayQty <= 2500 && dayQty > 0,
        'text-dark': dayQty === 0
      }">
        {{ dayQty }} {{ $t('ui.piecesAbbr') }}
      </small>
    </div>

    <div class="week-day-orders">
      <draggable
        v-model="dayItems"
        group="days"
        tag="div"
        @change="weekDayOrderChange">
        <d-card
          v-for="(item, index) of dayItems"
          :ref="'week-day-orders-item-' + dayIndex + '-' + index"
          :key="'week-day-orders-' + dayIndex + '-' + index"
          class="text-center p-3 mb-3 card-ui">
          <h3 :class="{
            'my-0': true,
            'text-danger': dayQty > 3500,
            'text-warning': dayQty > 2500 && dayQty <= 3500,
            'text-success': dayQty <= 2500,
            'text-success': dayQty <= 2500 && dayQty > 0,
            'text-dark': dayQty === 0
          }">
            {{ item.quantity }}
          </h3>
          <small class="py-2">
            {{ item.order_name }}
          </small>
          <div :class="{
            'text-center': true,
            'align-middle': true,
            'has-special-color': item.order_status === 'TO_CHECK'
          }">
            <d-badge
              :style="getOrderStatusStyle(item.order_status, item.order_is_priority)">
              {{ getOrderStatus(item.order_status) }}
            </d-badge>
          </div>

          <a
            href="#"
            class="order-move"
            @click.prevent="showMovePopup(item.id, item.quantity, item.date)">
            <i class="material-icons">
              trending_flat
            </i>
          </a>
        </d-card>
        <d-alert
          :show="!dayItems.length"
          theme="light"
          class="text-center">
          <small>
            {{ $t('calendar.noOrders') }}
          </small>
        </d-alert>
      </draggable>
    </div>
  </div>
</template>

<script>
import CalendarUtils from './../../utils/CalendarUtils';
import FormUtils from './../../utils/FormUtils.js';
import orderStatuses from './../../data/order-statuses.js';
import VueDraggable from 'vuedraggable';

export default {
  name: 'week-day',
  props: [
    'dayData',
    'dayIndex',
    'ordersData'
  ],
  components: {
    'draggable': VueDraggable
  },
  computed: {
    dayHumanReadableName () {
      return CalendarUtils.humanDateFormat(this.dayData.date, false, false);
    },
    dayName () {
      return CalendarUtils.getDayNameFromDate(this.dayData.date);
    },
    dayQty () {
      return this.dayItems.reduce((sum, item) => sum + item.quantity, 0);
    }
  },
  data () {
    return {
      dayItems: []
    };
  },
  mounted () {
    this.updateDayItems();
    this.$bus.$on('services-schedule-week-days-reload', this.updateDayItems);
  },
  methods: {
    getOrderStatus (status) {
      return orderStatuses[this.$i18n.locale][status] ? orderStatuses[this.$i18n.locale][status] : '??';
    },
    getOrderStatusStyle (status, hasPriority) {
      switch (status) {
        case 'TO_COLLECT': return 'dark';
        case 'WAITING_PURCHASE_PRODUCTS': return hasPriority ? 'danger' : 'primary';
        case 'ORDER': return hasPriority ? 'danger' : 'primary';
        case 'IN_PRODUCTION': return hasPriority ? 'danger' : 'primary';
        case 'TO_CHECK': return 'secondary';
        case 'PREPARATION_FOR_SHIPPING': return 'success';
        case 'WAITING_FOR_SHIPPING': return 'success';
        case 'SENT': return 'success';
        case 'PENDING_PAYMENT': return 'success';
        case 'ENDED': return 'success';
        default: return '';
      }
    },
    showMovePopup (itemID, maxQty, date) {
      this.$bus.$emit('services-schedule-open-move-popup', itemID, maxQty, date);
    },
    updateDayItems () {
      if (this.ordersData && this.ordersData.items && this.ordersData.items.length) {
        this.dayItems = JSON.parse(JSON.stringify(this.ordersData.items));
      } else {
        this.dayItems = []
      }
    },
    weekDayOrderChange (event) {
      if (event.moved) {
        this.$bus.$emit('services-schedule-week-days-ordering-save');
      } else if (event.added) {
        let newElement = this.dayItems.filter(item => item.id === event.added.element.id);

        if (newElement[0]) {
          this.$bus.$emit('services-schedule-week-order-change', true);
          newElement[0].date = this.dayData.date;

          FormUtils.submitInBackground({
            $http: this.$http,
            endpoint: '/services-schedule/update',
            formData: {
              items: [{
                action: 'update',
                id: newElement[0].id,
                date: newElement[0].date
              }]
            },
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('calendar.orderUpdateUnsuccessful'),
            successAction: () => {
              this.$bus.$emit('services-schedule-week-days-ordering-save');
            },
            failAction: () => {
              this.$bus.$emit('services-schedule-week-order-change', false);
            }
          });
        }
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('services-schedule-week-days-reload', this.updateDayItems);
  }
}
</script>

<style lang="scss" scoped>
.week-day {
  width: calc(100% / 7);

  .order-move {
    background: #fff;
    border-bottom: 1px solid #ddd;
    border-left: 1px solid #ddd;
    border-radius: 0 10px 0 10px;
    padding: 5px 10px;
    position: absolute;
    right: 0;
    top: 0;

    &:active,
    &:focus,
    &:hover {
      background: #eee;
    }
  }

  .badge {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  .has-special-color {
    .badge {
      background: #8445f7!important;
    }
  }
}
</style>
